import React from "react";
import styled from "styled-components";
import { colors } from "utils";
import chevronRightIcon from "assets/icons/lesson-report/chevron-right.svg";
import warningIcon from "assets/icons/lesson-report/warning.svg";

// The Critical Incidents block, shared verbatim between the lesson report card
// and the session report so the two surfaces cannot drift apart: one heading,
// one disclaimer, one tag vocabulary, one counts strip.

export const INCIDENTS_HEADING = "Potential Safety Concerns — Flagged For Teacher Review";

// The tab and pill-tag name for the same block, everywhere it appears.
export const INCIDENTS_TAB_LABEL = "Safety Concerns";

// Read by a teacher who is about to act on a flagged quote, so it says what the
// quote is worth as evidence before it says anything else. Wording is the
// human's, verbatim; it is safety guidance, not copy to be tuned.
const INCIDENTS_DISCLAIMER_TEXT =
  "Quotes are auto-transcribed and excerpted by AI. They may not be 100% " +
  "accurate and may lack context. We recommend reviewing the full transcript " +
  "before taking action. You should follow your school's protocol for " +
  "appropriate escalation and documentation of any flagged content.";

const OKO_MODERATION_URL =
  "https://docs.google.com/document/d/189EZZU14Zh82qdijfawnOc_vl8rZAQPG87cgiHQen_Q/edit?tab=t.0";

export const IncidentsDisclaimer = () => (
  <>
    {INCIDENTS_DISCLAIMER_TEXT}{" "}
    <DisclaimerLink href={OKO_MODERATION_URL} target="_blank" rel="noopener noreferrer">
      Learn more about how OKO Moderation works.
    </DisclaimerLink>
  </>
);

const DisclaimerLink = styled.a`
  color: ${colors.blue700};
  text-decoration: underline;

  &:hover {
    color: ${colors.blue800};
  }
`;

// The design's chip copy ("Misconduct") pending product confirmation — the
// backend tag vocabulary only has SELF_HARM and TOXIC.
export const INCIDENT_TAG_META = {
  "WELFARE:SELF_HARM": { label: "Self Harm", bg: colors.pink50, border: colors.pink300, text: colors.pink800 },
  "CLIMATE:TOXIC": { label: "Misconduct", bg: colors.amber50, border: "#EAB308", text: colors.amber800 },
};

export const incidentTagMeta = (tag) =>
  INCIDENT_TAG_META[tag] || { label: tag, bg: colors.amber50, border: "#EAB308", text: colors.amber800 };

export const TagChip = styled.span`
  display: inline-block;
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  border-radius: 11px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${(p) => p.$color};
  white-space: nowrap;
`;

export const IncidentTagChip = ({ tag }) => {
  const meta = incidentTagMeta(tag);
  return (
    <TagChip $bg={meta.bg} $border={meta.border} $color={meta.text}>
      {meta.label}
    </TagChip>
  );
};

// The header pill a lesson card or session row wears when it has incidents.
export const SafetyConcernsTag = () => (
  <SafetyConcernsChip $bg={colors.pink50} $border={colors.pink300} $color={colors.pink800}>
    <img src={warningIcon} alt="" aria-hidden="true" />
    {INCIDENTS_TAB_LABEL}
  </SafetyConcernsChip>
);

const SafetyConcernsChip = styled(TagChip)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  /* Hug the content — the flex-column parent stretches children by default. */
  align-self: flex-start;

  img {
    width: 16px;
    height: 16px;
  }
`;

/**
 * The at-a-glance strip: heading, one chip + count per tag, and a View Details
 * link into the full listing. `onViewDetails` absent (a surface with no
 * incidents view, e.g. the admin prompt console) hides the link.
 */
export const IncidentCountsSummary = ({ counts = [], onViewDetails, emptyText }) => (
  <>
    <SubheadRow>
      <SectionHeading>{INCIDENTS_HEADING}</SectionHeading>
      {counts.length > 0 && onViewDetails && (
        <ViewDetailsButton type="button" onClick={onViewDetails}>
          View Details <img src={chevronRightIcon} alt="" aria-hidden="true" />
        </ViewDetailsButton>
      )}
    </SubheadRow>
    {counts.length === 0 ? (
      <BodyText>{emptyText}</BodyText>
    ) : (
      <IncidentCountsRow>
        {counts.map(({ tag, count }) => (
          <IncidentCount key={tag}>
            <IncidentTagChip tag={tag} />
            <span>
              {count} {count === 1 ? "Instance" : "Instances"}
            </span>
          </IncidentCount>
        ))}
      </IncidentCountsRow>
    )}
  </>
);

const SectionHeading = styled.h3`
  font-family: "ABC Marfa", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${colors.nightlyWoods500};
  margin: 16px 0 0;
`;

const SubheadRow = styled.div`
  display: flex;
  /* Baseline, not center: the heading carries its own top margin, which makes
     centered links ride high against the heading text. */
  align-items: baseline;
  gap: 8px;
`;

export const ViewDetailsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-family: Inter, sans-serif;
  font-size: 18px;
  line-height: 26px;
  color: ${colors.blue700};
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const IncidentCountsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 24px 0;
`;

const IncidentCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};
`;

const BodyText = styled.p`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};
  margin: 8px 0 0;
`;
